import { useContext, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import {
    ClearInputIcon,
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import InputFieldWithDropdown from "components/DesignComponents/InputFieldWithDropdown";
import { DropdownListItem } from "components/DesignComponents/InputFieldWithDropdown/types";
import ParamsContext from "context/params/paramsContext";
import TableContext from "context/table/tableContext";
import { useDebounce } from "shared/hooks/useDebounce";
import { useMediaQueries } from "shared/hooks/useMediaQueries";
import { useParamsFromUrlAndBrowser } from "shared/hooks/useParamsFromUrlAndBrowser";
import { RectangleLoader } from "shared/loaders/RectangleLoader";
import { NOT_FILTER_NAMES } from "shared/types/enums";

import { useFields } from "./hooks";
import { SearchProps } from "./types";

const Search = ({ isDisabled }: SearchProps) => {
    const { setQueryParams } = useContext(ParamsContext);

    const {
        isInnerTable,
        tableName,
        innerTableQueryParams,
        isInitialLoading,
        setInnerTableParams
    } = useContext(TableContext);

    const { t } = useTranslation();
    const { currentParams } = useParamsFromUrlAndBrowser();
    const { toMd } = useMediaQueries();

    const { activeQueryFields, storageKeyName, setActiveQueryField } =
        useFields(
            tableName,
            isInnerTable,
            innerTableQueryParams,
            currentParams
        );

    const queryField = useMemo(
        () => setActiveQueryField(),
        // eslint-disable-next-line
        [currentParams, isInnerTable, innerTableQueryParams]
    );

    const query = useMemo(() => {
        return (
            ((isInnerTable
                ? innerTableQueryParams[NOT_FILTER_NAMES.Query]
                : currentParams[NOT_FILTER_NAMES.Query]) as string) || ""
        );
    }, [currentParams, isInnerTable, innerTableQueryParams]);

    const [value, setValue] = useState(query);

    const debouncedValue = useDebounce(value, 500);

    // Sets value from the Find tool
    useEffect(() => {
        if (query !== value) {
            setValue(query);
        }
        // eslint-disable-next-line
    }, [query]);

    useEffect(() => {
        if (query !== value) {
            updateParams({
                params: {
                    [NOT_FILTER_NAMES.Query]: debouncedValue,
                    [NOT_FILTER_NAMES.QueryField]: queryField.value
                }
            });
        }
        // eslint-disable-next-line
    }, [debouncedValue, queryField.value]);

    const setNewQueryField = (newField: DropdownListItem) => {
        localStorage.setItem(storageKeyName, newField.value);

        updateParams({
            params: {
                [NOT_FILTER_NAMES.Query]: query,
                [NOT_FILTER_NAMES.QueryField]: newField.value
            }
        });
    };

    const updateParams = isInnerTable ? setInnerTableParams : setQueryParams;

    const changeValue = (e: { target: { value: string } }) => {
        const newValue = e.target.value;

        setValue(newValue);
    };

    const clearValue = () => setValue("");

    return isInitialLoading ? (
        <RectangleLoader
            customStyle={{
                maxWidth: "290px",
                width: "100%",

                "@media(max-width: 839px)": {
                    maxWidth: "unset",
                    width: "unset",
                    flex: "1"
                },

                "@media(max-width: 599px)": {
                    flex: "unset",
                    width: "100%"
                }
            }}
            height={36}
        />
    ) : (
        <InputFieldWithDropdown
            fullWidth={toMd}
            size={INPUT_FIELD_SIZES.Small}
            value={value}
            disabled={isDisabled}
            onChange={e => changeValue(e)}
            iconRight={<ClearInputIcon value={value} clearValue={clearValue} />}
            placeholder={t("Search##search by")}
            dropdownList={activeQueryFields}
            selectedListItem={queryField}
            onListChoiceClick={setNewQueryField}
            containerCustomStyles={{
                "@media(max-width: 839px)": {
                    flex: "1"
                },

                "@media(max-width: 599px)": {
                    flex: "unset",
                    width: "100%"
                }
            }}
        />
    );
};

export default Search;
